import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Search,
  Bot,
  ClipboardCheck,
  MapPin,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

export default function Home() {
  const [address, setAddress] = useState("");
  const [prompt, setPrompt] = useState("");
  const [purpose, setPurpose] = useState("explore");
  const [location, setLocation] = useState("");
  const navigate = useNavigate();
  const startConversation = (event, starter) => {
    event?.preventDefault();
    const question = (starter || prompt).trim();
    if (!question) return;
    const query = new URLSearchParams({ question, purpose });
    if (location.trim()) query.set("location", location.trim());
    navigate(`/ask?${query.toString()}`);
  };
  return (
    <div className="home-page">
      <section className="premium-image-bar" aria-labelledby="premium-image-heading">
        <img
          src="/images/queenslander.jpg"
          alt="Queenslander home surrounded by established subtropical gardens"
          loading="eager"
          width="900"
          height="1350"
        />
        <div className="premium-image-shade" aria-hidden="true" />
        <div className="premium-image-copy">
          <span className="eyebrow">Guidance, grounded in evidence</span>
          <h1 id="premium-image-heading">A beautiful property deserves a clear-eyed decision.</h1>
          <p>Begin with a conversation. Move into sourced research when the detail matters.</p>
        </div>
        <div className="premium-image-signals" aria-label="How Prop AI supports a decision">
          <article><Sparkles size={19} /><span>Ask naturally</span><small>Start with your real question</small></article>
          <article><ShieldCheck size={19} /><span>Verify precisely</span><small>Evidence and gaps stay visible</small></article>
          <article><ClipboardCheck size={19} /><span>Act clearly</span><small>Know what to check next</small></article>
        </div>
        <span className="premium-image-caption">AI-generated architectural illustration</span>
      </section>
      <section className="home-hero">
        <div className="hero-copy">
          <span className="eyebrow">AI-guided property research · Australia</span>
          <h2 className="home-ai-title">
            Ask first.
            <br />
            Decide with evidence.
          </h2>
          <p>
            Start with any property question. Prop AI helps you frame the decision, compare what matters and move into verified research when an address is needed.
          </p>
          <div className="home-ai-proof"><span><Sparkles size={16} /> No perfect question required</span><span><MapPin size={16} /> Address optional</span></div>
          <form
            className="home-search"
            onSubmit={(e) => {
              e.preventDefault();
              navigate(
                `/research?${new URLSearchParams({ address: address.trim() })}`,
              );
            }}
          >
            <label className="sr-only" htmlFor="home-address">
              Property address
            </label>
            <Search size={20} aria-hidden="true" />
            <input
              id="home-address"
              required
              minLength={8}
              maxLength={300}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Or enter a Brisbane property address"
              autoComplete="street-address"
            />
            <button>
              Research property <ArrowRight size={18} aria-hidden="true" />
            </button>
          </form>
          <Link className="text-link" to="/research">
            See how verified research works <ArrowRight size={16} />
          </Link>
          <p className="pilot-note">
            Brisbane pilot · Coverage varies by address.
          </p>
        </div>
        <section className="home-ai-stage" aria-labelledby="home-ai-heading">
          <header><span className="assistant-avatar"><Bot size={22} /></span><div><h2 id="home-ai-heading">Ask Prop AI</h2><p><i /> Online · your property co-pilot</p></div></header>
          <div className="home-ai-intro"><span className="eyebrow">Start with what you are trying to decide</span><h2>What are you thinking about?</h2><p>Ask naturally. I’ll help organise the question, show what can be answered now and identify what needs verified evidence.</p></div>
          <form onSubmit={startConversation} className="home-ai-composer">
            <div className="home-ai-fields"><label>My goal<select value={purpose} onChange={(event) => setPurpose(event.target.value)}><option value="explore">Just exploring</option><option value="first_home">First-home buyer</option><option value="buy">Buying a home</option><option value="invest">Investing</option><option value="sell">Selling</option><option value="own">Owning or reviewing</option></select></label><label>Suburb or region <small>optional</small><input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="e.g. Taigum" maxLength={160} /></label></div>
            <label className="sr-only" htmlFor="home-ai-question">Ask a property question</label>
            <div className="home-ai-question"><textarea id="home-ai-question" rows={3} value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="Ask about a suburb, buying costs, rental yield, an offer, selling, or what to check next…" maxLength={1200} /><button aria-label="Start conversation" disabled={!prompt.trim()}><Send size={20} /></button></div>
          </form>
          <div className="home-ai-starters" aria-label="Example questions">{["Compare two suburbs", "What can I afford to overlook?", "Explain rental yield", "Plan my first-home search"].map((item) => <button type="button" key={item} onClick={(event) => startConversation(event, item)}>{item}<ArrowRight size={14} /></button>)}</div>
          <p className="home-ai-trust">General guidance first. Current and address-specific claims are separated into sourced research.</p>
        </section>
      </section>
      <section className="home-how">
        <span className="eyebrow">A little clarity goes a long way</span>
        <h2>From an address to a clearer decision.</h2>
        <div className="steps-grid">
          {[
            [
              "Understand the evidence",
              "See what is available for your address, with sources and gaps clearly identified.",
            ],
            [
              "Explore the numbers",
              "Review postcode rent benchmarks and model purchase costs using your assumptions.",
            ],
            [
              "Know what to check",
              "Keep the unanswered questions in view before taking your next step.",
            ],
          ].map(([title, copy], i) => (
            <article key={title}>
              <span className="step-number">0{i + 1}</span>
              <div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="home-suburbs">
        <div>
          <span className="eyebrow">Start local</span>
          <h2>Explore Brisbane.</h2>
          <p>
            Get familiar with the research available for your next property.
          </p>
          <Link className="text-link" to="/suburbs">
            All suburb guides <ArrowRight size={18} />
          </Link>
        </div>
        <div className="suburb-links">
          {["White Rock", "Walloon", "Taigum"].map((name, i) => (
            <Link
              key={name}
              to={`/suburbs/${name.toLowerCase().replace(/ /g, "-")}`}
            >
              <span className="eyebrow">0{i + 1} / R2 corridor</span>
              <strong>{name}</strong>
              <span>
                Explore the guide <ArrowRight size={18} />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
